import React from "react";
import { motion } from "framer-motion";

/**
 * ReusableBanner
 * Props:
 * - bgImage: string (url of background image)
 * - title: string or ReactNode
 * - subtitle: string or ReactNode
 * - height: Tailwind height class (e.g. "h-96", "h-[80vh]")
 * - fixedAttachment: boolean (if true background-attachment: fixed)
 * - overlay: string (Tailwind color class for overlay, e.g. "bg-black/40")
 * - align: "left" | "center" | "right"
 * - cta: { text, href, onClick }
 * - children: React children (optional)
 */

export default function ReusableBanner({
  bgImage = "",
  title = "Your headline here",
  subtitle = "Optional subtitle",
  height = "h-70vh md:h-[60vh]",
  fixedAttachment = true,
  overlay = "bg-black/40",
  align = "center",
  cta = null,
  children = null,
}) {
  const alignment =
    align === "left" ? "items-start text-left" : align === "right" ? "items-end text-right" : "items-center text-center";

  return (
    <section
      className={`relative w-full overflow-hidden ${height}`}
      style={{
        backgroundImage: bgImage ? `url(${bgImage})` : "none",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: fixedAttachment ? "fixed" : "scroll",
      }}
      aria-label="banner"
    >
      {/* optional blurred overlay */}
      <div className={`absolute inset-0 ${overlay}`} />

      {/* content wrapper */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 flex h-full">
        <motion.div
  className={`w-full flex flex-col justify-center ${alignment} py-12 md:py-24`}
  initial={{ opacity: 0, y: 40 }}
  whileInView={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.8, ease: "easeOut" }}
  viewport={{ once: true, amount: 0.4 }}
>
  <motion.h1
    className="text-3xl md:text-5xl font-extrabold leading-tight text-white"
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.8, ease: "easeOut" }}
    viewport={{ once: true }}
  >
    {title}
  </motion.h1>

  {subtitle && (
    <motion.p
      className="mt-4 max-w-3xl text-xl md:text-2xl text-white/90"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2, duration: 0.8, ease: "easeOut" }}
      viewport={{ once: true }}
    >
      {subtitle}
    </motion.p>
  )}

  {cta && (
    <motion.div
      className={`mt-6 flex ${align === "left" ? "justify-start" : align === "right" ? "justify-end" : "justify-center"}`}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4, duration: 0.8, ease: "easeOut" }}
      viewport={{ once: true }}
    >
      <motion.a
        href={cta.href || "#"}
        onClick={cta.onClick}
        className="inline-flex items-center gap-3 rounded-2xl px-6 py-3 text-sm font-semibold shadow-lg bg-white/90 text-gray-900 backdrop-blur-sm hover:scale-[1.02] transform-gpu transition"
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.98 }}
      >
        {cta.text || "Learn more"}
      </motion.a>
    </motion.div>
  )}

  {children && (
    <motion.div
      className="mt-6"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.6, duration: 0.8, ease: "easeOut" }}
      viewport={{ once: true }}
    >
      {children}
    </motion.div>
  )}
</motion.div>

      </div>

   

    </section>
  );
}
